/* eslint-disable max-len */
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  EstimateSettingsFilter,
  LaborType,
  SettingsField,
} from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateSettings';
import { Settings } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetSettings';
import { Garage } from '@1po/1po-bff-fe-spec/generated/garage/Garage';
import { MotrioLogoIcon } from 'assets/icons';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import { useFetchEstimateSettingsSubscription } from 'domains/estimate/Estimate.requests';
import { getEstimateLogo, getEstimateSettings, setEstimateLogo, updateSettings } from 'domains/estimate/Estimate.store';
import { FocusSetting } from 'domains/estimate/Estimate.types';
import { getUserGarageInfo } from 'domains/garage/Garage.store';
import { formatAddress, getFirstValidAddress } from 'domains/garage/Garage.utils';
import { getCurrency, getUserDisplayMode } from 'domains/user';
import { getSettingsLaborTimeRate } from 'pages/CatalogPage/DH/SubcategorySection/LaborTimeSection/utils';
import { ChangeEmail } from 'pages/SettingsPage/Estimates/ChangeEmail';
import { BrandCheckBox } from 'pages/SettingsPage/Estimates/EstimateSettings.styled';
import {
  ItemContentWrapper,
  ItemWrapper,
  SectionItemsWrapper,
  SectionWrapper,
} from 'pages/SettingsPage/ProfilePage.styled';
import {
  Box,
  CleverInput,
  Flex,
  Icon,
  InputNumber,
  InputPrice,
  MarginBox,
  Select,
  Space,
  Spin,
  Text,
  TextArea,
  WideTabs,
} from 'UI';
import { getData, NO_DATA, NOT_FOUND } from 'utils';
import { useOffsetTop } from 'utils/hooks/useOffsetTop';
import { scrollToTopSmooth } from 'utils/hooks/useResetScroll';

interface SettingsSectionItem {
  key: string;
  title: string | ReactNode;
  description?: string | ReactNode;
  component: ReactNode;
  viewStyle?: boolean;
  required?: boolean;
}

interface SettingsSectionData {
  title: ReactNode;
  items?: SettingsSectionItem[];
  description?: ReactNode;
  data: Garage | Settings | NO_DATA;
  viewStyle?: boolean;
  inline?: boolean;
  borderless?: boolean;
}

export const SETTINGS_RATES = 'labor_time_rates';

const SettingsSection = ({
  data: { items, title, description, data, viewStyle, inline, borderless },
  maxW,
  id,
}: {
  data: SettingsSectionData;
  maxW?: number;
  id?: string;
}) => (
  <Flex>
    <SectionWrapper borderless={borderless}>
      {title && (
        <Text type="heading4" id={id}>
          {title}
        </Text>
      )}
      <MarginBox mt={10} />
      {description && (
        <>
          {description}
          {items && <MarginBox mt={30} />}
        </>
      )}
      <DataContainer
        data={data}
        ErrorState={() => (
          <Box height={200}>
            <ErrorWithLabel />
          </Box>
        )}
      >
        <SectionItemsWrapper
          isViewStyle={viewStyle}
          direction={inline ? 'row' : 'column'}
          gap={inline ? 25 : 0}
          wrap={'wrap'}
        >
          {items?.map(
            ({
              title: itemTitle,
              component,
              viewStyle: itemViewStyle,
              description: itemDescription,
              required,
              key,
            }) => (
              <ItemWrapper isViewStyle={itemViewStyle} key={`item-${key}`}>
                <ItemContentWrapper maxWidth={maxW}>
                  {itemTitle && (
                    <Flex direction={'column'} padding={'0 0 6px 0'}>
                      {typeof itemTitle === 'string' ? (
                        <Text type="base" bold>
                          {itemTitle}
                          {required && (
                            <Text type="base" color="red">
                              {' *'}
                            </Text>
                          )}
                        </Text>
                      ) : (
                        itemTitle
                      )}
                      {itemDescription && (
                        <Flex>
                          {typeof itemDescription === 'string' ? (
                            <Text type="sm" color="grey55">
                              {itemDescription}
                            </Text>
                          ) : (
                            itemDescription
                          )}
                        </Flex>
                      )}
                    </Flex>
                  )}
                  <Box>{component}</Box>
                </ItemContentWrapper>
              </ItemWrapper>
            ),
          )}
        </SectionItemsWrapper>
      </DataContainer>
    </SectionWrapper>
  </Flex>
);

const brandLogos = [
  {
    id: 'motrio',
    icon: MotrioLogoIcon,
  },
];

const initialSettings: Settings = {
  apeCode: '',
  intraCommunityVat: '',
  vatRate: undefined,
  estimateValidFor: undefined,
  validityUnit: 'MONTH',
  observations: '',
  legalMentions: '',
};

export const EstimateSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  useFetchEstimateSettingsSubscription();
  const garageInfo = useSelector(getUserGarageInfo);
  const garageData = getData(garageInfo);
  const address = garageData ? getFirstValidAddress(garageData) : undefined;
  const settings = useSelector(getEstimateSettings);
  const estimateLogo = useSelector(getEstimateLogo);
  const isR3Motrio = useSelector(getUserDisplayMode) === 'R3_MOTRIO';
  const offsetTop = useOffsetTop('ESTIMATE');
  const currency = useSelector(getCurrency);
  //eslint-disable-next-line
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [settingsState, setSettingsState] = useState<Settings | undefined>(getData(settings));
  const focusSetting = params.get(FocusSetting);
  if (focusSetting) {
    setTimeout(() => {
      const focusElement = document.getElementById(focusSetting);
      if (focusElement) {
        scrollToTopSmooth(focusElement.offsetTop - offsetTop);
      }
    }, 100);
  }
  const handleUpdate = (field: SettingsField, filters?: EstimateSettingsFilter) => (
    newValue: string | number | undefined,
  ) => {
    dispatch(updateSettings({ field, newValue: newValue === null ? '' : String(newValue), filters }));
  };

  const handleUpdateNumber = (field: SettingsField) => (newValue: string | number | undefined) => {
    dispatch(updateSettings({ field, newValue: newValue ? String(newValue) : String(0) }));
  };

  const handleInputNumberOnKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/^\d$/.test(e.key) &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Escape', 'Enter', 'Home', 'End'].includes(e.key)
    ) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (settings === NOT_FOUND) {
      setSettingsState(initialSettings);
    }
    if (getData(settings)) {
      setSettingsState(getData(settings));
    }
  }, [settings]);

  const companyContactDetails: SettingsSectionData = {
    title: t('estimate.settings.company_contact_details', 'Company contact details'),
    description: (
      <Text type="base">
        {t(
          'estimate.settings.company_contact_details.description',
          'Some info may be visible to other people using Rpartstore',
        )}
      </Text>
    ),
    items: [
      {
        key: 'company-1',
        title: t('estimate.settings.social_reason', 'Social reason:'),
        component: <Text type="base">{garageData?.name ?? ''}</Text>,
      },
      {
        key: 'company-2',
        title: t('estimate.settings.address', 'Address:'),
        component: <Text type="base">{formatAddress(address)}</Text>,
      },
      {
        key: 'company-3',
        title: t('estimate.settings.postal_code', 'Postal code:'),
        component: <Text type="base">{address?.zipCode ?? ''}</Text>,
      },
      {
        key: 'company-4',
        title: t('estimate.settings.city', 'City:'),
        component: <Text type="base">{address?.city ?? ''}</Text>,
      },
      {
        key: 'company-5',
        title: t('estimate.settings.phone', 'Phone:'),
        component: <Text type="base">{address?.phone ?? ''}</Text>,
      },
      {
        key: 'company-7',
        title: t('estimate.settings.email', 'Email:'),
        component: <ChangeEmail email={address?.email} />,
      },
    ],
    viewStyle: true,
    data: garageInfo,
  };

  const administrativeInformation: SettingsSectionData = {
    title: t('estimate.settings.administrative_information', 'Administrative information'),
    description: (
      <Text type="base">
        {t(
          'estimate.settings.administrative_information.description',
          'Some info may be visible to other people using Rpartstore',
        )}
      </Text>
    ),
    items: [
      {
        key: 'administrative-1',
        title: t('estimate.settings.company_registration_number', 'Company registration number:'),
        component: <Text type="base">XXXXXXXXXX-XXX</Text>,
        viewStyle: true,
      },
      {
        key: 'administrative-2',
        title: t('estimate.settings.ape_code', 'APE Code:'),
        description: t(
          'estimate.settings.ape_code.description',
          'Identifies the main branch of activity on both companies',
        ),
        required: true,
        component: (
          <CleverInput
            size={'middle'}
            onChangeDebounced={handleUpdate('APE_CODE')}
            initialValue={settingsState?.apeCode ?? ''}
            bordered
          />
        ),
      },
      {
        key: 'administrative-3',
        title: t('estimate.settings.intra_community_vat', 'Intra-community TVA number:'),
        description: t('estimate.settings.intra_community_vat.description', 'Individual identification number'),
        required: true,
        component: (
          <CleverInput
            size={'middle'}
            onChangeDebounced={handleUpdate('INTRA_COMMUNITY_VAT')}
            initialValue={settingsState?.intraCommunityVat ?? ''}
            bordered
          />
        ),
      },
    ],
    data: settingsState,
  };

  const VATRate: SettingsSectionData = {
    title: t('estimate.settings.vat_rate_title', 'VAT rate'),
    description: (
      <Text type="base">
        {t('estimate.settings.for_estimates_only', 'For Estimates (only for Labor Time and Waste Recycling)')}
      </Text>
    ),
    items: [
      {
        key: 'vat-1',
        title: t('estimate.settings.vat_rate', 'VAT rate:'),
        required: true,
        component: (
          <InputNumber
            value={settingsState?.vatRate ?? undefined}
            onChangeDebounced={handleUpdate('VAT_RATE')}
            status={settingsState?.vatRate === 0 ? 'error' : undefined}
            precisionMax={2}
            addonAfter={'%'}
            min={0}
            max={100}
            bordered
          />
        ),
      },
    ],
    data: settingsState,
  };

  const commercialEstimateValidity: SettingsSectionData = {
    title: t('estimate.settings.commercial_estimate_validity', 'Commercial estimate validity'),
    items: [
      {
        key: 'validity-1',
        title: t('estimate.settings.commercial_estimate_validity.validity', 'Validity'),
        description: t('estimate.settings.estimates_valid', 'The estimates generated will be valid:'),
        required: true,
        component: (
          <Flex align={'center'}>
            <Box>
              <InputNumber
                onChangeDebounced={handleUpdateNumber('ESTIMATE_VALIDITY')}
                value={settingsState?.estimateValidFor ?? undefined}
                min={0}
                max={settingsState?.validityUnit === 'WEEK' ? 12 : 3}
                status={settingsState?.estimateValidFor === 0 ? 'error' : undefined}
                bordered
                onKeyPress={handleInputNumberOnKeyPress}
              />
            </Box>
            <Box width={25} />
            <Box width={150}>
              <Select
                onChange={handleUpdate('ESTIMATE_VALIDITY_UNIT')}
                placeholder={'Month'}
                options={[
                  {
                    title: t('estimate.settings.week', 'Week', { count: settingsState?.estimateValidFor }),
                    value: 'WEEK',
                  },
                  {
                    title: t('estimate.settings.month', 'Month', { count: settingsState?.estimateValidFor }),
                    value: 'MONTH',
                  },
                ]}
                value={settingsState?.validityUnit}
                bordered
                styleLess
              />
            </Box>
          </Flex>
        ),
      },
    ],
    data: settingsState,
  };
  const observations: SettingsSectionData = {
    title: t('estimate.settings.observations', 'Observations'),
    description: (
      <TextArea
        onChangeDebounced={handleUpdate('OBSERVATIONS')}
        placeholder={t('estimate.settings.add_observation_here', 'Add your observations here.')}
        size={'large'}
        initialValue={settingsState?.observations ?? ''}
        maxLength={180}
      />
    ),
    data: settingsState,
  };
  const legalMentions: SettingsSectionData = {
    title: t('estimate.settings.legal_mentions', 'Legal mentions'),
    description: (
      <TextArea
        onChangeDebounced={handleUpdate('LEGAL_MENTIONS')}
        placeholder={t('estimate.settings.add_legal_mention_here', 'Add legal mentions here.')}
        size={'large'}
        initialValue={settingsState?.legalMentions ?? ''}
      />
    ),
    data: settingsState,
  };

  const pdfLogo: SettingsSectionData = {
    title: t('estimate.settings.pdf_logo.title', 'Logo to display on your Estimates'),
    description: (
      <Flex direction={'column'}>
        <Text type="base">
          {t(
            'estimate.settings.pdf_logo.description',
            'Please select the brand logo that should be displayed on your estimates.',
          )}
        </Text>
        <MarginBox mt={15} />
        <Space size={30}>
          {brandLogos.map((brand) => {
            return (
              <BrandCheckBox
                key={brand.id}
                onClick={() => dispatch(setEstimateLogo(brand.id))}
                $selected={brand.id === estimateLogo}
              >
                <Icon IconComponent={brand.icon} width={90} height={63} />
              </BrandCheckBox>
            );
          })}
        </Space>
      </Flex>
    ),
    data: undefined,
  };

  const getLaborTimeRates = (laborType: LaborType): SettingsSectionData => {
    return {
      title: null,
      inline: true,
      borderless: true,
      items: [
        {
          key: `labor-time-${laborType}-1`,
          title: t('estimate.settings.labor_time_excl_vat_rate.low_technicity', 'Low technicity:'),
          required: true,
          component: (
            <InputPrice
              value={getSettingsLaborTimeRate(laborType, 'NORMAL', settingsState?.laborPriceList)?.price}
              onChangeDebounced={handleUpdate('LABOR_PRICE', {
                laborType,
                laborLevel: 'NORMAL',
              })}
              placeholder={t(
                'estimate.settings.labor_time_excl_vat_rate.low_technicity.placeholder',
                'T1 technicity rate',
              )}
              currency={currency}
            />
          ),
        },
        {
          key: `labor-time-${laborType}-2`,
          title: t('estimate.settings.labor_time_excl_vat_rate.high_technicity', 'High technicity:'),
          required: true,
          component: (
            <InputPrice
              value={getSettingsLaborTimeRate(laborType, 'HIGH', settingsState?.laborPriceList)?.price}
              onChangeDebounced={handleUpdate('LABOR_PRICE', { laborType, laborLevel: 'HIGH' })}
              placeholder={t(
                'estimate.settings.labor_time_excl_vat_rate.high_technicity.placeholder',
                'T2 technicity rate',
              )}
              currency={currency}
            />
          ),
        },
        {
          key: `labor-time-${laborType}-3`,
          title: t('estimate.settings.labor_time_excl_vat_rate.very_high_technicity', 'Very high technicity:'),
          required: true,
          component: (
            <InputPrice
              value={getSettingsLaborTimeRate(laborType, 'VERY_HIGH', settingsState?.laborPriceList)?.price}
              onChangeDebounced={handleUpdate('LABOR_PRICE', { laborType, laborLevel: 'VERY_HIGH' })}
              placeholder={t(
                'estimate.settings.labor_time_excl_vat_rate.very_high_technicity.placeholder',
                'T3 technicity rate',
              )}
              currency={currency}
            />
          ),
        },
      ],
      data: settingsState,
    };
  };

  const tabData = [
    {
      title: t('estimate.settings.labor_time_excl_vat_rate.mechanics.tab_name', 'Mechanics'),
      key: 'mechanics',
      component: <SettingsSection data={getLaborTimeRates('MECHANICS')} maxW={270} />,
    },
    {
      title: t('estimate.settings.labor_time_excl_vat_rate.painting.tab_name', 'Painting'),
      key: 'painting',
      component: <SettingsSection data={getLaborTimeRates('PAINTING')} maxW={270} />,
    },
    {
      title: t('estimate.settings.labor_time_excl_vat_rate.bodywork.tab_name', 'Bodywork'),
      key: 'bodywork',
      component: <SettingsSection data={getLaborTimeRates('PANELWORK')} maxW={270} />,
    },
    {
      title: t('estimate.settings.labor_time_excl_vat_rate.electricity.tab_name', 'Electricity'),
      key: 'electricity',
      component: <SettingsSection data={getLaborTimeRates('ELECTRICITY')} maxW={270} />,
    },
    {
      title: t('estimate.settings.labor_time_excl_vat_rate.trim.tab_name', 'Trim'),
      key: 'trim',
      component: <SettingsSection data={getLaborTimeRates('TRIM')} maxW={270} />,
    },
  ];

  const laborTime: SettingsSectionData = {
    title: t('estimate.settings.labor_time_excl_vat_rate.title', 'Labor time Excl.VAT rate'),
    description: (
      <Text type={'light_14'}>
        {t(
          'estimate.settings.labor_time_excl_vat_rate.description',
          'Please note that only Mechanics technicity are mandatory. If Painting, Bodywork, Electricity and Trim technicity are not filled, we will simply use Mechanics as default setting.',
        )}
      </Text>
    ),
    items: [
      {
        key: `labor-time-1`,
        title: null,
        component: (
          <Box>
            <WideTabs tabData={tabData} variant={'blueActiveWhite'} />
          </Box>
        ),
      },
    ],
    data: settingsState,
  };

  return (
    <Flex direction={'column'} gap={25} padding={'25px 0'}>
      <Flex wrap={'wrap'} gap={25}>
        <SettingsSection data={companyContactDetails} key={'section-contact'} />
        {settingsState && (
          <SettingsSection data={administrativeInformation} maxW={300} key={'section-administratives'} />
        )}
      </Flex>
      {!settingsState ? (
        <Flex justify={'center'}>
          <Spin />
        </Flex>
      ) : (
        <>
          <SettingsSection data={laborTime} maxW={1000} key={'section-labor-time-rate'} id={SETTINGS_RATES} />
          <SettingsSection data={VATRate} key={'section-vat'} maxW={200} />
          <SettingsSection data={commercialEstimateValidity} maxW={370} key={'section-estimate-validity'} />
          {isR3Motrio && <SettingsSection data={pdfLogo} key={'section-pdf-logo'} />}
          <SettingsSection data={observations} key={'section-observations'} />
          <SettingsSection data={legalMentions} key={'section-legal'} />
        </>
      )}
    </Flex>
  );
};
